import React from "react";
import "./SideBar.css";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentUser, logOut } from "../../features/auth/authSlice";
import logo from "../../assets/images/Logo.png";
import logOutIcon from "../../assets/images/icons/logout.svg";
import { apiSlice } from "../../app/api/apiSlice";
import { userRoles } from "../../enums/userRoles";
import linkIcon from "../../assets/images/icons/link.svg";
import { useMsal } from "@azure/msal-react";

const SideBar = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.roles?.includes(userRoles.ADMIN);
  const isNavLinkActive = (path) => location.pathname.includes(path);

  const handleLogout = async () => {
    dispatch(apiSlice.util.resetApiState());
    dispatch(logOut());
    instance.clearCache();
    await instance.logoutRedirect({
      postLogoutRedirectUri: "/login",
      onRedirectNavigate: () => false,
    });
  };

  const rolesMap = {
    [userRoles.ADMIN]: "Admin",
    [userRoles.ENHANCED_MANAGER]: "Enhanced Manager",
    [userRoles.MANAGER]: "Manager",
    [userRoles.SENIOR_INCIDENT_MANAGER]: "Senior Incident Manager",
    [userRoles.LOCAL_INCIDENT_MANAGER]: "Local Incident Manager",
    [userRoles.INCIDENT_ADVISOR_OR_LEADER]: "Incident Advisor or Leader",
  };

  const userRoleTitle =
    rolesMap[user?.roles?.find((role) => rolesMap[role])] || "Standard";

  return (
    <div className="sidebar-container">
      <div>
        <div className="nav-brand text-center logo-container">
          <img src={logo} alt="barnardo's logo" className="logo" />
        </div>
        <div className="list-group list-group-flush scrollarea menu-link">
          <NavLink
            to="/my-tasks"
            className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link task-link`}
          >
            <span>{isAdmin ? "" : "My"} Tasks</span>
          </NavLink>
          <NavLink
            to="/incidents"
            className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link incident-link`}
          >
            <span>{isAdmin ? "" : "My"} Incidents</span>
          </NavLink>
          {user && isAdmin ? (
            <>
              <div className={``}>
                <button
                  className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link admin-link ${
                    isNavLinkActive("/admin/task-library") ||
                    isNavLinkActive("/admin/incident-tasks") ||
                    isNavLinkActive("/admin/manage-users")
                      ? "active"
                      : ""
                  }`}
                  onClick={() => navigate("/admin")}
                  data-bs-toggle="collapse"
                  data-bs-target="#admin-collapse"
                  aria-expanded="true"
                >
                  <span>Admin</span>
                </button>
                <div className="collapse sub-menu" id="admin-collapse">
                  <NavLink
                    to="admin/manage-users"
                    className={`list-group-item slist-group-item-action lh-sm rounded`}
                  >
                    <span>Manage Users</span>
                  </NavLink>
                  <NavLink
                    to="admin/task-library"
                    className={`list-group-item list-group-item-action lh-sm rounded`}
                  >
                    <span>Task Library</span>
                  </NavLink>
                  <NavLink
                    to="admin/incident-tasks"
                    className={`list-group-item list-group-item-action lh-sm rounded`}
                  >
                    <span>Task Automation</span>
                  </NavLink>
                </div>
              </div>
            </>
          ) : (
            ""
          )}
          <NavLink
            to="https://hub.barnardos.org.uk/portal/kb?text=sort&entity=all&id=38"
            className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link url-link faq-link`}
            target="_blank"
            aria-label="FAQ - Frequently  Asked Questions (opens in new tab)"
          >
            <span>FAQ</span>
          </NavLink>
          <NavLink
            to="https://hub.barnardos.org.uk/portal/service?btn=73&itil_requesttype_id=3&root_category=22&autolog=true&shownewticket=true&categoryid=10&id=68"
            className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link url-link issue-link`}
            target="_blank"
            aria-label="Need help? (opens in new tab)"
          >
            <span>Need help?</span>
          </NavLink>
          <div>
            <button
              className="list-group-item list-group-item-action lh-sm rounded sidebar-nav-link emergency-contact-link"
              data-bs-toggle="collapse"
              data-bs-target="#emergency-contact-collapse"
              aria-expanded="true"
            >
              <span>Emergency Contacts</span>
            </button>
            <div className="sub-menu collapse" id="emergency-contact-collapse">
              <NavLink
                to="https://hub.barnardos.org.uk/portal/kb?btn=61&faqlist=31&id=61"
                className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link url-link ps-3`}
                target="_blank"
                aria-label="Health & Safety emergency contacts (opens in new tab)"
              >
                <span>Health & Safety</span>
              </NavLink>
              <NavLink
                to="https://hub.barnardos.org.uk/portal/kb?btn=61&faqlist=31&id=65"
                className={`list-group-item list-group-item-action lh-sm rounded sidebar-nav-link url-link ps-3`}
                target="_blank"
                aria-label="Safeguarding emergency contacts (opens in new tab)"
              >
                <span>Safeguarding</span>
              </NavLink>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="avatar-dropdown-container mb-1">
          <div className="avatar-container">
            <span className="avatar-img">
              {`${user?.firstName[0]}${user?.lastName[0]}`}
            </span>
          </div>
          <div
            className={`user-menu ${
              user?.roles?.includes(userRoles.ADMIN) ? "admin" : ""
            } `}
          >
            <div className="d-flex flex-column text-start">
              <span className="fw-bold">{userRoleTitle}</span>
              <span> {`${user?.firstName} ${user?.lastName}`}</span>
            </div>
            <button
              className="btn btn-user align-self-stretch"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
            >
              <img src={logOutIcon} width={28} alt="logout icon" />
            </button>
          </div>
        </div>
        <NavLink
          className="version-text "
          to={"/release-notes"}
          target="_blank"
        >
          <small>
            v{process.env.REACT_APP_VERSION}
            <img
              className="ms-1"
              src={linkIcon}
              width={20}
              alt="Application version"
            />
          </small>
        </NavLink>
      </div>
    </div>
  );
};

export default SideBar;
